/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import ManagedCollectionsModule from 'bundles/browse-common/components/ComplexCollectionComponent/ManagedCollectionsModule';
import { initialComplexCollection } from 'bundles/browse-common/components/ComplexCollectionComponent/initialComplexCollection';
import { toCarouselCollection } from 'bundles/browse-common/components/ComplexCollectionComponent/toCarouselCollection';
import { toCollectionsModuleFilter } from 'bundles/browse-common/components/ComplexCollectionComponent/toCollectionsModuleFilter';
import { useQueryBrowseComplexCollection } from 'bundles/browse-common/components/ComplexCollectionComponent/useBrowseComplexCollection';
import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';
import type { ComplexCollection, ComplexCollectionFilter } from 'bundles/browse-common/types/complexCollections';
import type { PageConfig_CollectionComponentMetadataFragment as PageConfigCollectionComponentMetadata } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_CollectionComponentMetadata';

type Props = {
  title: string;
  filters: ComplexCollectionFilter[];
  firstCollection: ComplexCollection;
  secondaryCta?: PageConfigCollectionComponentMetadata['secondaryCta'];
  pageConfigComponentTrackingData?: PageConfigComponentTrackingData;
  twoColumnLayout?: boolean;
  hideSecondaryCta?: boolean;
  dataTestId?: string;
};

const styles = {
  heading: css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

const ComplexCollectionComponent = (props: Props) => {
  const {
    title,
    filters,
    firstCollection,
    secondaryCta,
    pageConfigComponentTrackingData,
    twoColumnLayout,
    hideSecondaryCta,
    dataTestId,
  } = props;

  const [collections, setCollections] = useState(initialComplexCollection(firstCollection, filters));
  const track = useTracker();

  const query = useQueryBrowseComplexCollection();

  const [loading, setLoading] = useState(false);

  const collectionsModuleFilters = filters.map(toCollectionsModuleFilter);

  const collectionsModuleCollections = collections.map((collection) =>
    toCarouselCollection(collection, pageConfigComponentTrackingData)
  );

  const handleFilterChange = async (index: number, filterName: string) => {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    if (!collections[index].entities) {
      setLoading(true);
      const filter = filters[index];
      try {
        const result = await query({
          // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          queryCollectionId: filter.context.id,
          // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          additionalInput: filter.context.additionalInput,
        });
        const collection: ComplexCollection | undefined = result.data.Recommendations?.queryCollection;
        if (collection) {
          const newCollections = [...collections];
          newCollections[index] = collection;
          setCollections(newCollections);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSectionHeadingCtaClick = () => {
    track('click_button', {
      button: {
        name: 'collections_secondary_cta',
        linkName: secondaryCta?.ctaTrackingName ?? '',
        linkURL: secondaryCta?.ctaUrl ?? '',
        ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
      },
    });
  };

  const testId = dataTestId || `collection-${firstCollection.id}`;

  return (
    <ManagedCollectionsModule
      alwaysShowChipFilters
      filters={collectionsModuleFilters}
      collections={collectionsModuleCollections}
      isCollectionsLoading={loading}
      onFilterChange={handleFilterChange}
      layout="text-top/image-bottom"
      isPageGridContainerAppliedOnPage={twoColumnLayout}
      customItemsGridConfig={twoColumnLayout ? { lg: 4, md: 4, sm: 6, xs: 12 } : undefined}
      customHeadline={
        <SectionHeadingComponent
          additionalStyles={styles.heading}
          ctaTitle={secondaryCta?.ctaTitle}
          ctaUrl={secondaryCta?.ctaUrl}
          onClickCta={handleSectionHeadingCtaClick}
          hideSecondaryCta={hideSecondaryCta}
        >
          {title}
        </SectionHeadingComponent>
      }
      eventingData={{ pageConfigComponent: pageConfigComponentTrackingData }}
      dataTestId={testId}
    />
  );
};

export default ComplexCollectionComponent;
